import { createContext, useCallback, useContext, useEffect, useReducer, useRef, } from "react";
import { unstable_batchedUpdates } from "react-dom";
// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop() { }
export const context = createContext(null);
export const { Provider } = context;
function useForceUpdate() {
    // dispatch don't have action and don't changes between rerenders
    return useReducer((s) => s + 1, 0)[1];
}
const lifeCycleStatus = {
    init: "init",
    upToDate: "upToDate",
    outOfDate: "outOfDate",
};
const defaultMapper = (atomValue) => atomValue;
/**
 * @param ctx react context for your store.
 * @returns A `useAtom` hook bound to the context.
 */
export function createAtomHook(ctx = context) {
    function useAtom(atom, selector = defaultMapper, deps = []) {
        const forceUpdate = useForceUpdate();
        const store = useContext(ctx);
        if (!store)
            throw new Error("[reatom] The provider is not defined");
        const { current: that } = useRef({
            store,
            atom: undefined,
            selector,
            deps,
            atomValue: undefined,
            selectorValue: undefined,
            unsubscribe: noop,
            mountStatus: lifeCycleStatus.init,
        });
        that.selector = selector;
        if (store !== that.store || atom !== that.atom) {
            that.store = store;
            that.atom = atom;
            that.deps = deps;
            that.unsubscribe();
            that.unsubscribe = store.subscribe(that.atom, (atomValue) => {
                if (Object.is(that.atomValue, atomValue))
                    return;
                that.atomValue = atomValue;
                const selectorValue = that.selector(atomValue);
                if (Object.is(selectorValue, that.selectorValue))
                    return;
                that.selectorValue = selectorValue;
                if (that.mountStatus === lifeCycleStatus.init) {
                    that.mountStatus = lifeCycleStatus.outOfDate;
                }
                else {
                    forceUpdate();
                }
            });
            that.atomValue = that.store.getState(atom);
            that.selectorValue = that.selector(that.atomValue);
        }
        else {
            const atomValue = that.store.getState(that.atom);
            if (!Object.is(atomValue, that.atomValue) ||
                deps.length !== that.deps.length ||
                deps.some((d, i) => !Object.is(d, that.deps[i]))) {
                that.deps = deps;
                that.atomValue = atomValue;
                that.selectorValue = that.selector(that.atomValue);
            }
        }
        useEffect(() => {
            if (that.mountStatus === lifeCycleStatus.outOfDate) {
                that.mountStatus = lifeCycleStatus.upToDate;
                forceUpdate();
            }
            that.mountStatus = lifeCycleStatus.upToDate;
            return () => that.unsubscribe();
        }, []);
        return that.selectorValue;
    }
    return useAtom;
}
// https://github.com/artalar/reatom/tree/v1/packages/react
/**
 * @param atom target atom for subscription
 * @param selector (optional)
 * @param deps (optional)
 * @returns atom value
 */
export const useAtom = createAtomHook();
/**
 * @param ctx react context for your store.
 * @returns A `useAction` hook bound to the context.
 */
export function createActionHook(ctx = context) {
    function useAction(cb, deps = []) {
        const store = useContext(ctx);
        if (!store)
            throw new Error("[reatom] The provider is not defined");
        if (typeof cb !== "function") {
            throw new TypeError("[reatom] `useAction` argument must be a function");
        }
        return useCallback((...args) => {
            const action = cb(...args);
            if (action) {
                const storeFixed = Object.assign({}, store, {
                    dispatch(nextAction) {
                        const actionFixed = Object.assign({}, nextAction, {
                            reactions: (nextAction.reactions || []).map((reaction) => (payload) => reaction(payload, storeFixed)),
                        });
                        unstable_batchedUpdates(() => store.dispatch(actionFixed));
                    },
                });
                storeFixed.dispatch(action);
            }
        }, deps.concat(store));
    }
    return useAction;
}
/**
 * @param cb actionCreator (may return void for preventing dispatch)
 * @param deps
 */
export const useAction = createActionHook();
// TODO: TS tests
// const a = declareAction()
// const ap = declareAction<0>()
// const aop = declareAction<{ a: string; b: number; c: boolean }>()
// const test1 = useAction(a)
// const test2 = useAction(() => a())
// const test3 = useAction(ap)
// const test4 = useAction(() => ap(0))
// const test5 = useAction<0>((v) => ap(v))
// const test6 = useAction<0>((v) => a())
// const test7 = useAction(() => {
//   if (Math.random()) {
//     return ap(0)
//   }
//   if (Math.random()) {
//     return a()
//   }
//   if (Math.random()) {
//     // error
//     return 123
//   }
// })
// const test8 = useAction((a: string, b: number, c: boolean = false) => aop({ a, b, c }))
